import { Ellipsis } from 'components/elements/Ellipsis';
import { Icon } from 'components/elements/Icon';
import { css, styled } from 'styles';
import { StyledSelectInputProps, StyledSelectProps } from './types';

const boxShadow = css`
  box-shadow: ${({ theme }) => theme.shadow.highlight};
`;

const disabledStyles = css`
  cursor: default;
  opacity: 0.5;
`;

const expandedStyles = css`
  ${boxShadow};

  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom-color: ${({ theme }) => theme.select.border};
  border-left-color: ${({ theme }) => theme.select.focus.border};
  border-right-color: ${({ theme }) => theme.select.focus.border};
  border-top-color: ${({ theme }) => theme.select.focus.border};
  z-index: 2;
`;

const focusStyles = css`
  ${boxShadow};

  border-color: ${({ theme }) => theme.select.focus.border};
`;

export const SelectIcon = styled(Icon)`
  color: ${({ theme }) => theme.select.icon};
  margin-left: ${({ theme }) => theme.spacing.relative.small};
  transition: transform ${({ theme }) => theme.transition.slow} ease-in-out;
`;

export const SelectInput = styled.div<StyledSelectInputProps>`
  align-items: center;
  background-color: ${({ theme }) => theme.select.background};
  border: thin solid ${({ theme }) => theme.select.border};
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.select.color};
  cursor: pointer;
  display: flex;
  height: ${({ theme }) => theme.element.height}px;
  line-height: ${({ theme }) => theme.element.lineHeight};
  outline: none;
  padding: 0 ${({ theme }) => theme.spacing.relative.smaller};
  position: relative;
  transition: border-color ${({ theme }) => theme.transition.medium} ease-in-out,
    box-shadow ${({ theme }) => theme.transition.medium} ease-in-out;
  user-select: none;

  ${({ $disabled }) => $disabled && disabledStyles};
  ${({ $expanded }) => $expanded && expandedStyles};

  &:disabled {
    opacity: 0.5;
  }

  ${SelectIcon} {
    transform: rotateX(${({ $expanded }) => ($expanded ? '180deg' : '0deg')});
  }
`;

export const SelectInputText = styled(Ellipsis)`
  flex: 1;
`;

export const SelectOptionItem = styled(Ellipsis)`
  align-items: center;
  border-bottom: thin solid ${({ theme }) => theme.select.option.border};
  cursor: pointer;
  display: flex;
  height: ${({ theme }) => theme.element.height}px;
  line-height: ${({ theme }) => theme.element.lineHeight};
  padding: 0 ${({ theme }) => theme.spacing.relative.smaller};
  transition: background-color ${({ theme }) => theme.transition.fast} ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.select.option.selected.background};
  }
`;

export const SelectOptionListItems = styled.div`
  max-height: ${({ theme }) => `${theme.element.height * 8}px`};
`;

export const SelectOptionList = styled.div`
  ${boxShadow};

  background-color: ${({ theme }) => theme.select.option.background};
  border: thin solid ${({ theme }) => theme.select.focus.border};
  border-top: none;
  border-bottom-left-radius: ${({ theme }) => theme.radius.small};
  border-bottom-right-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.select.color};
  outline: none;
  position: absolute;
  user-select: none;
  width: 100%;

  .ps--active-y {
    ${SelectOptionListItems} {
      padding-right: ${({ theme }) =>
        theme.scrollbar.thumb.size + theme.scrollbar.track.spacing * 2}px;
    }
  }
`;

export const SelectPlaceholder = styled(Ellipsis)`
  color: ${({ theme }) => theme.select.placeholder.color};
  flex: 1;
`;

export const StyledSelect = styled.div<StyledSelectProps>`
  font-size: 1rem;
  outline: none;
  position: relative;
  z-index: ${({ $expanded }) => ($expanded ? 2 : undefined)};

  &:focus {
    ${SelectInput} {
      ${({ $disabled, $expanded }) => !$disabled && !$expanded && focusStyles};
    }
  }
`;

SelectIcon.displayName = 'SelectIcon';
SelectInput.displayName = 'SelectInput';
SelectInputText.displayName = 'SelectInputText';
SelectOptionItem.displayName = 'SelectOptionItem';
SelectOptionList.displayName = 'SelectOptionList';
SelectOptionListItems.displayName = 'SelectOptionListItems';
SelectPlaceholder.displayName = 'SelectPlaceholder';
StyledSelect.displayName = 'Select.Styled';
